import { Box } from '@mui/material';
import { GridColDef, GridColumnVisibilityModel, GridPreProcessEditCellProps, GridRowModel, useGridApiRef } from '@mui/x-data-grid';
import DataGrid from 'components/data-grid';
import Switch from 'components/switch';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Pond } from 'views/production-systems/types/pond.type';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import { calculateBiomass } from 'views/production-systems/utils/farm-overview';
import { isValidDecimal, isValidInteger, isValidMeanWeight, isValidValue } from 'utils/validate-numbers';
import Inactive from 'views/production-systems/assets/Inactive';
import Active from 'views/production-systems/assets/Active';
import { DestinationRow } from 'views/production-systems/features/farm-overview/types/destination-row.type';
import { SourceRow } from 'views/production-systems/features/farm-overview/types/source-row-type';

type Props = {
  selectedPond: Pond | undefined;
  sourceRow: SourceRow;
  setSourceRow: React.Dispatch<React.SetStateAction<SourceRow>>;
  isPartialTransfer: boolean;
  setIsPartialTransfer: React.Dispatch<React.SetStateAction<boolean>>;
  destinationRows: DestinationRow[]; // Needed for remainingQty calculation
};

const PAGE_NAME_ID = 'productionSystems.farmOverview.transfer';

const SourcePonds: React.FC<Props> = ({
  selectedPond,
  sourceRow,
  setSourceRow,
  isPartialTransfer,
  setIsPartialTransfer,
  destinationRows,
}) => {
  const intl = useIntl();
  const sourceDataGridApiRef = useGridApiRef();
  const { errorDictionary, changeErrorDictionary, handleEditing } = useDataGridError();
  const sourcePond = selectedPond as Pond;

  const [sourceColumnVisibility, setSourceColumnVisibility] = useState<GridColumnVisibilityModel>({
    newCycleName: isPartialTransfer,
    remainingQty: isPartialTransfer,
    remainingMw: isPartialTransfer,
    remainingBm: isPartialTransfer,
    pondName: true,
    cycleName: true,
    currentQty: true,
    currentMw: true,
    currentBm: true,
  });

  useEffect(() => {
    setSourceColumnVisibility({
      ...sourceColumnVisibility,
      newCycleName: isPartialTransfer,
      remainingQty: isPartialTransfer,
      remainingMw: isPartialTransfer,
      remainingBm: isPartialTransfer,
    });
  }, [isPartialTransfer]);

  const processRowUpdate = (newRow: GridRowModel<SourceRow>) => {
    const updatedRow = { ...newRow, remainingBm: calculateBiomass(newRow.remainingMw, newRow.remainingQty) };

    setSourceRow(updatedRow as SourceRow);

    return updatedRow;
  };

  useEffect(() => {
    if (isPartialTransfer) {
      const qty = destinationRows.reduce((prev, curr) => prev + curr.transferQty, 0);
      const newRemainingQty = sourcePond.currentQty - qty;

      setSourceRow((row) => ({
        ...row,
        remainingQty: newRemainingQty,
        remainingBm: calculateBiomass(row.remainingMw, newRemainingQty),
      }));
    }
  }, [destinationRows, isPartialTransfer, setSourceRow, sourcePond.currentQty]);

  const sourceColumns: GridColDef[] = [
    {
      field: 'pondName',
      flex: 2,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.source.sourcePond` }),
      editable: false,
      renderCell: ({ row }) => {
        return (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              width: '100%',
            }}>
            {!!row.cycleId ? <Active titleAccess="Active" /> : <Inactive titleAccess="Inactive" />}
            <Box
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={row.pondName}>
              {row.pondName}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'cycleName',
      flex: 1,
      type: 'string',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.source.cycleName` }),
      editable: false,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'currentQty',
      flex: 1,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.source.currentNumberAdd` }),
      editable: false,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'currentMw',
      flex: 1,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.source.currentMw` }),
      type: 'number',
      editable: false,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'currentBm',
      flex: 1,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.source.currentBm` }),
      headerClassName: !isPartialTransfer ? '-borderRadius' : '',
      editable: false,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'newCycleName',
      flex: 1,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.source.newCycleName` }),
      type: 'string',
      editable: false,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const hasError = params.props.value === '';
        changeErrorDictionary({ ...errorDictionary, [`${params.id}-newCycleName`]: hasError });
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'remainingQty',
      flex: 1,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.source.remainingQty` }),
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      editable: false,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const hasError = isPartialTransfer ? !isValidInteger(params.props.value) : false;
        changeErrorDictionary({ ...errorDictionary, [`${params.id}-remainingQty`]: hasError });
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'remainingMw',
      flex: 1,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.source.remainingMw` }),
      editable: true,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const hasError = !isValidDecimal(params.props.value) || (isPartialTransfer && isValidValue(params.props.value)) || !isValidMeanWeight(params.props.value);
        changeErrorDictionary({ ...errorDictionary, [`${params.id}-remainingMw`]: hasError });
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'remainingBm',
      flex: 1,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.source.remainingBm` }),
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      editable: false,
    },
  ];

  return (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <Box display={'flex'} flexDirection={'row'} gap={4}>
        <FormattedMessage id={`${PAGE_NAME_ID}.sourcePond`} />
        <Switch
          size="small"
          dark
          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.fields.partialTransfer` })}
          value={isPartialTransfer}
          onChange={() => setIsPartialTransfer((prev) => !prev)}
        />
      </Box>
      <DataGrid
        autoHeight
        hideFooter
        rows={[sourceRow]}
        columns={sourceColumns}
        apiRef={sourceDataGridApiRef}
        onCellEditStop={handleEditing}
        getRowId={(row) => row.pondId}
        processRowUpdate={processRowUpdate}
        columnVisibilityModel={sourceColumnVisibility}
        onRowClick={({ id }) => {
          const { field } = sourceDataGridApiRef.current?.state.tabIndex.cell ?? {};
          sourceDataGridApiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
        }}
      />
    </Box>
  );
};

export default SourcePonds;
